













import {Component, Mixins, Prop, Ref, Watch} from 'vue-property-decorator'
import {Portal} from '@linusborg/vue-simple-portal'
import MonitorMixin from "@/design-system/utilities/MonitorMixin"

/**
 * Using this? Make sure you follow ARIA recommendations for custom inputs!
 * See:
 *  - https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html
 *  - https://www.w3.org/TR/wai-aria-practices-1.1/examples/menubar/menubar-1/menubar-1.html
 *  - https://www.w3.org/TR/wai-aria-practices-1.1/examples/menubar/menubar-2/menubar-2.html
 *
 */
@Component({
    components: {SimplePortal: Portal}
})
export default class JdhAttachedModalPopup extends Mixins(MonitorMixin) {

    @Prop({default: false, type: Boolean}) visible!: boolean
    @Prop() anchor!: HTMLElement

    @Prop({default: false, type: Boolean}) fullWidth!: boolean
    @Prop({default: 'auto'}) xAlign!: 'auto'|'left'|'right'|'center'

    @Ref() popupEl!: HTMLElement
    @Ref() screenEl!: HTMLElement

    raf: number | null = null

    additionalStyles = {
        width: 'max-content',
        transform: '',
    }

    @Watch('visible')
    syncListeners() {
        if (this.visible) {
            document.addEventListener('click', this.handleAnyClick)
            document.addEventListener('keyup', this.handleGlobalKeyup)
            document.addEventListener('scroll', this.updatePosition, {
                capture: true,
                passive: true,
            })
            window.addEventListener('resize', this.updatePosition, {
                capture: true,
                passive: true,
            })
        } else {
            // TODO: manage cleanup separately
            document.removeEventListener('click', this.handleAnyClick)
            document.removeEventListener('keyup', this.handleGlobalKeyup)
            document.removeEventListener('scroll', this.updatePosition)
            window.removeEventListener('resize', this.updatePosition)
        }
    }

    @Watch('visible')
    attachToAnchor() {
        if (this.visible) {
            if (!this.anchor) {
                this.$warn('No anchor available, popup is unable to position itself.', 'NO_ANCHOR')
                return
            }

            this.updatePosition()
            requestAnimationFrame(() => {
                this.$emit('opened')
            })
        }
    }

    updatePosition() {
        if (this.raf) return

        this.raf = requestAnimationFrame(this.renderPosition)
    }

    renderPosition() {
        if (!this.anchor || !this.popupEl || !this.screenEl) {
            // this function may be called after these elements have disappeared due to the animation frame buffering.
            this.raf = null
            return
        }

        const anchorBox = this.anchor.getBoundingClientRect()
        let popupBox = this.popupEl.getBoundingClientRect()
        const screenBox = this.screenEl.getBoundingClientRect()
        let x, y

        if (this.fullWidth || anchorBox.left + (anchorBox.width / 2) <= screenBox.width / 2) {
            // left side
            x = anchorBox.left
        } else {
            // right side
            x = anchorBox.right - popupBox.width
        }

        // force alignment... When absolutely necessary only, of course.
        if (!this.fullWidth) {
            switch (this.xAlign) {
                case "left": {
                    x = anchorBox.left
                    break
                }
                case "right": {
                    x = anchorBox.right - popupBox.width
                    break
                }
                case "center": {
                    x = anchorBox.left + (anchorBox.width * 0.5) - (popupBox.width * 0.5)
                    break
                }
            }
        }

        if (this.popupEl.firstElementChild!.scrollHeight > screenBox.height) {
            this.popupEl.style.height = `${screenBox.height - this.gapWidth * 2}px`;
            popupBox.height = screenBox.height - this.gapWidth * 2;
        } else {
            this.popupEl.style.height = '';
            popupBox = this.popupEl.getBoundingClientRect();
        }

        if (popupBox.height + this.gapWidth > screenBox.height - (anchorBox.top + anchorBox.height)) {
            // flip upwards, as it'll clip the bottom otherwise
            y = Math.max(
                anchorBox.top - popupBox.height - this.gapWidth,
                screenBox.top + this.gapWidth,
            );
        } else {
            y = anchorBox.top + anchorBox.height + this.gapWidth
        }

        // handle full-width
        if (this.fullWidth) {
            this.additionalStyles.width = anchorBox.width + 'px'
        }

        this.additionalStyles.transform = `translate3d(${x}px, ${y}px, 0px)`
        this.raf = null
    }

    handleAnyClick(e: MouseEvent) {
        if (this.visible && this.popupEl && e.target) {
            if (!this.popupEl.contains(e.target as HTMLElement) && (this.anchor ? !this.anchor.contains(e.target as HTMLElement) : true)) {
                this.$debug('Click outside of anchor or popup, dismissing.')
                this.$emit('dismiss', e)
            }
        }
    }

    handleGlobalKeyup(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            this.$emit('dismiss', e)
        }
    }

    get gapWidth() {
        return 5;
    }
}
